import { useNavigate } from "react-router-dom";
import { MaskedLine, Reveal } from "./motion";
import { trackEvent } from "../analytics/ga4";
import { scrollToAnchor } from "../utils/scroll";

export const PageHero = ({ label, title, sub, testId }) => (
  <section
    data-testid={testId}
    className="grain relative overflow-hidden bg-deep pb-28 pt-44 text-white sm:pb-36 sm:pt-56"
  >
    <div className="absolute inset-0 opacity-50">
      <img
        src="/images/brand-atmosphere.jpg"
        alt=""
        className="animate-drift h-full w-full object-cover object-[75%_50%] sm:object-center"
        loading="eager"
      />
    </div>
    <div className="absolute inset-0 bg-deep/35" />
    <div className="relative mx-auto max-w-[1600px] px-6 sm:px-12 md:px-16">
      <Reveal y={16}>
        <p className="text-[13px] font-medium uppercase tracking-label text-wave">{label}</p>
      </Reveal>
      <h1 className="font-head mt-8 max-w-4xl text-4xl leading-[1.08] tracking-tight sm:text-6xl lg:text-7xl">
        <MaskedLine delay={0.15}>{title}</MaskedLine>
      </h1>
      {sub && (
        <Reveal delay={0.5} className="mt-10 max-w-xl">
          <p className="text-base font-light leading-relaxed text-white/70 sm:text-lg">{sub}</p>
        </Reveal>
      )}
    </div>
  </section>
);

export const QuoteBand = ({ image, alt, line1, line2, testId, position = "center", sideText }) => (
  <section data-testid={testId} className={`relative overflow-hidden ${image ? "" : "grain bg-[#F3F7F9]"}`}>
    {image && (
      <div className="absolute inset-0">
        <img
          src={image}
          alt={alt}
          className="h-full w-full object-cover"
          style={{ objectPosition: position }}
          loading="lazy"
        />
        <div className="absolute inset-0 bg-deep/55" />
      </div>
    )}
    <div className="relative mx-auto grid max-w-[1600px] gap-10 px-6 py-32 sm:px-12 sm:py-44 md:grid-cols-12 md:px-16">
      <blockquote className={`font-head max-w-3xl text-3xl leading-snug sm:text-5xl md:col-span-7 ${image ? "text-white" : "text-ink"}`}>
        <MaskedLine>{line1}</MaskedLine>
        {line2 && (
          <MaskedLine delay={0.15}>
            <span className={image ? "text-wave" : "text-deep"}>{line2}</span>
          </MaskedLine>
        )}
      </blockquote>
      {sideText && (
        <Reveal delay={0.3} className="md:col-span-4 md:col-start-9 md:self-end">
          <p data-testid="quote-band-side-text" className={`text-base font-light leading-relaxed sm:text-lg ${image ? "text-white/70" : "text-ink/70"}`}>
            {sideText}
          </p>
        </Reveal>
      )}
    </div>
  </section>
);

export const PageCta = ({ title, text }) => (
  <section data-testid="page-cta" className="bg-paper">
    <div className="mx-auto max-w-[1600px] px-6 py-28 sm:px-12 sm:py-36 md:px-16">
      <div className="max-w-2xl">
        <Reveal>
          <h2 className="font-head text-3xl leading-tight text-ink sm:text-5xl">{title}</h2>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-6 text-base font-light leading-relaxed text-ink/70 sm:text-lg">{text}</p>
        </Reveal>
        <Reveal delay={0.3}>
          <CtaButton />
        </Reveal>
      </div>
    </div>
  </section>
);

export const CtaButton = () => {
  const navigate = useNavigate();

  return (
    <a
      href="/#kontakt"
      data-testid="cta-contact-button"
      onClick={(e) => {
        e.preventDefault();
        trackEvent("contact_cta_click", { placement: "page_cta" });
        if (window.location.pathname !== "/") {
          navigate("/#kontakt");
        } else {
          scrollToAnchor("kontakt");
        }
      }}
      className="group mt-10 inline-flex items-center gap-4 border border-ink/25 px-8 py-4 text-[11px] font-medium uppercase tracking-[0.22em] text-ink transition-all duration-500 hover:bg-ink hover:text-white"
    >
      Kontakt
      <span className="h-px w-6 bg-current transition-all duration-500 group-hover:w-10" />
    </a>
  );
};
